import React from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Typography, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import DatePicker from '../../Components/DatePicker/date-picker.tsx';
import { useNavigate } from 'react-router-dom';
import { Trip } from './types';

const card3DStyle = {
    background: 'linear-gradient(145deg, #ffffff, #f6f6f6)',
    borderRadius: '18px',
    boxShadow: `0 8px 20px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.05)`,
    transition: 'all 0.3s ease',
    transformStyle: 'preserve-3d',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '18px',
        background: 'linear-gradient(120deg, rgba(183,28,28,0.15), transparent)',
        opacity: 0,
        transition: 'opacity 0.3s ease',
    },
    '&:hover': {
        transform: 'translateY(-6px) scale(1.01)',
        boxShadow: `0 14px 30px rgba(0,0,0,0.12), 0 6px 12px rgba(0,0,0,0.08)`,
    },
    '&:hover::before': { opacity: 1 },
    '&:active': { transform: 'translateY(-2px) scale(0.99)' },
};

interface TripsMobileViewProps {
    filteredTrips: Trip[];
    currentTabs: string[];
    activeMobileFilter: string;
    handleTabChange: (val: string) => void;
    dynamicStatus: string;
    statusMenuAnchor: HTMLElement | null;
    setStatusMenuAnchor: (el: HTMLElement | null) => void;
    tripOptions: string[];
    handleStatusSelect: (status: string) => void;
    searchTerm: string;
    handleInputChange: (e: any) => void;
    handleSearchClick: () => void;
    showMobileFilters: boolean;
    setShowMobileFilters: (val: boolean | ((prev: boolean) => boolean)) => void;
    fuelTypeOptions: any[];
    selectedFuelType: string;
    handleFuelChange: (e: any) => void;
    warehouseOptions: any[];
    selectedWarehouse: any;
    handleWarehouseChange: (e: any) => void;
    selectedFromDate: string;
    selectedToDate: string;
    handleDateChange: (e: any) => void;
    handleMobileScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    activeTripId: number | null;
    handleRefreshLocation: (e: React.MouseEvent, id: string) => void;
    handleCall: (phone?: string) => void;
    setSelectedEditTrip: (trip: Trip) => void;
    setEditModalOpen: (val: boolean) => void;
    formatAddress: (loc: string | null) => string;
    vehicleList: any[];
}
const TripsMobileView: React.FC<TripsMobileViewProps> = ({
    filteredTrips, currentTabs, activeMobileFilter, handleTabChange, dynamicStatus,
    statusMenuAnchor, setStatusMenuAnchor, tripOptions, handleStatusSelect, searchTerm,
    handleInputChange, handleSearchClick, showMobileFilters, setShowMobileFilters,
    fuelTypeOptions, selectedFuelType, handleFuelChange, warehouseOptions, selectedWarehouse,
    handleWarehouseChange, selectedFromDate, selectedToDate, handleDateChange,
    handleMobileScroll, activeTripId, handleRefreshLocation, handleCall,
    setSelectedEditTrip, setEditModalOpen, formatAddress, vehicleList
}) => {
    const navigate = useNavigate();

    return (
        <div style={{ height: 'calc(100vh - 75px)', display: 'flex', flexDirection: 'column', background: ' linear-gradient(45deg, black, #ed1b1b)', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#f5f5f5', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', zIndex: 10 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, overflowX: 'auto', pr: 1 }}>
                    {currentTabs.map((filter) => (
                        <Button
                            key={filter}
                            variant="contained"
                            onClick={() => handleTabChange(filter)}
                            sx={{
                                background: activeMobileFilter === filter ? 'linear-gradient(45deg, black, #ed1b1b)' : '#e0e0e0',
                                color: activeMobileFilter === filter ? '#fff' : '#000',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                padding: '4px 8px 4px 12px',
                                whiteSpace: 'nowrap',
                                minWidth: 'fit-content',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {filter}
                            {filter === dynamicStatus && (
                                <Box component="span" onClick={(e) => { e.stopPropagation(); setStatusMenuAnchor(e.currentTarget); }} sx={{ display: 'flex', alignItems: 'center', ml: 0.5, borderLeft: activeMobileFilter === filter ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(0,0,0,0.1)', pl: 0.5 }}>
                                    <ArrowDropDownIcon fontSize="small" />
                                </Box>
                            )}
                        </Button>
                    ))}
                    <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)}>
                        {tripOptions.map((status) => (
                            <MenuItem key={status} selected={dynamicStatus === status} onClick={() => handleStatusSelect(status)}>
                                {status}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: '4px', border: '1px solid #ccc', px: 1, ml: 0.5, width: '100px' }}>
                        <input placeholder="Search..." value={searchTerm} onChange={handleInputChange} style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.75rem', height: '24px' }} />
                        <SearchIcon onClick={handleSearchClick} sx={{ fontSize: '14px', color: '#666' }} />
                    </Box>
                    <IconButton size="small" onClick={() => setShowMobileFilters((prev: boolean) => !prev)} sx={{ ml: 0.5 }}>
                        <i className="fas fa-filter" style={{ fontSize: '14px' }}></i>
                    </IconButton>
                </Box>
            </Box>

            {showMobileFilters && (
                <Box sx={{ backgroundColor: '#ffffff', padding: '12px', borderBottom: '1px solid #ddd', display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 9 }}>
                    <Dropdown options={fuelTypeOptions} selectedValue={selectedFuelType} handleChange={handleFuelChange} placeholder="Choose Fuel Type" label="Choose Fuel Type" width="100%" height="45px" />
                    <Dropdown options={warehouseOptions} selectedValue={selectedWarehouse?.id || ''} handleChange={handleWarehouseChange} placeholder="Choose Warehouse" label="Choose Warehouse" width="100%" height="45px" />
                    <Box sx={{ display: 'flex', gap: '10px', width: '100%', marginLeft: '-10px' }}>
                        <Box sx={{ flex: 1 }}>
                            <DatePicker id="fromDate" name="fromDate" value={selectedFromDate} label="From Date" onChange={handleDateChange} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <DatePicker id="toDate" name="toDate" value={selectedToDate} label="To Date" onChange={handleDateChange} />
                        </Box>
                    </Box>
                </Box>
            )}

            <Box sx={{ backgroundColor: '#e0e0e0', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                <Box onScroll={handleMobileScroll} sx={{ flex: 1, overflowY: 'auto', paddingBottom: '80px', background: 'linear-gradient(45deg, black, #ed1b1b)', margin: '5px', borderRadius: '10px' }}>
                    {filteredTrips.length === 0 ? (
                        <Box p={3} textAlign="center">
                            <Typography color="textSecondary" sx={{ color: 'white' }}></Typography>
                        </Box>
                    ) : (
                        filteredTrips.map((trip) => {
                            const isEnded = trip.status?.toLowerCase().includes('ended');
                            const isStarted = trip.status?.toLowerCase().includes('started');
                            const isAbsent = trip.status?.toLowerCase() === 'absent';
                            const isInactive = activeMobileFilter?.toLowerCase() === 'inactive';
                            const hasNoTripData = !trip.start_time;
                            const hasDriver = !!(trip.driver_name && trip.driver_phone);
                            const canEdit = !isAbsent && !isInactive && !isStarted && !hasNoTripData;
                            let locationIconColor = '#2e7d32';
                            if (isEnded) locationIconColor = '#d32f2f';
                            if (isAbsent) locationIconColor = '#616161';
                            const getParts = (dateStr: string) => {
                                if (!dateStr) return { time: '--:--', date: '--/--/----' };
                                const d = new Date(dateStr);
                                const day = String(d.getDate()).padStart(2, '0');
                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                const year = d.getFullYear();
                                return {
                                    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
                                    date: `${day}-${month}-${year}`
                                };
                            };

                            const startParts = getParts(trip.start_time);
                            const endParts = getParts(trip.end_time);
                            const manualDiff = (trip.odometer_end && trip.odometer_start) ? (Number(trip.odometer_end) - Number(trip.odometer_start)) : null;
                            const displayDistance = (manualDiff !== null && manualDiff >= 0) ? manualDiff.toFixed(2) : (trip.gps_kms ? Number(trip.gps_kms).toFixed(2) : '0.00');

                            let ordersDisplayValue = "";
                            let timeDistanceContent: React.ReactNode = null;

                            if (isEnded) {
                                ordersDisplayValue = `${trip.orders_picked || 0} | ${trip.orders_delivered || 0}`;
                                timeDistanceContent = (
                                    <Box display="grid" gridTemplateColumns="auto auto auto" columnGap="5px" alignItems="center">
                                        <span style={{ fontWeight: 600, textAlign: 'right' }}>{startParts.time}</span>
                                        <span style={{ color: '#999', textAlign: 'center' }}>|</span>
                                        <span style={{ fontWeight: 600, textAlign: 'left', color: 'red' }}>{endParts.time}</span>
                                        <span style={{ fontSize: '0.75em', color: '#666', textAlign: 'right' }}>{startParts.date}</span>
                                        <span style={{ fontSize: '0.75em', color: '#ccc', textAlign: 'center' }}>|</span>
                                        <span style={{ fontSize: '0.75em', color: '#666', textAlign: 'left' }}>{endParts.date}</span>
                                    </Box>
                                );
                            } else if (isAbsent) {
                                ordersDisplayValue = "N/A";
                                timeDistanceContent = <span>N/A</span>;
                            } else {
                                ordersDisplayValue = `${trip.orders_picked || 0}`;
                                timeDistanceContent = (
                                    <Box display="flex" flexDirection="column" lineHeight={1.2}>
                                        <span style={{ fontWeight: 600 }}>{startParts.time} | {displayDistance} km</span>
                                        <span style={{ fontSize: '0.75em', color: '#666' }}>{startParts.date}</span>
                                    </Box>
                                );
                            }

                            return (
                                <Box
                                    key={`${trip.id}-${trip.status}`}
                                    onClick={() => {
                                        navigate('/vehicles/directions', { state: { tripId: trip.id, vehicleNo: trip.number_plate, vehiclesId: trip.vehicles_id, driverName: trip.driver_name, startLat: trip.start_latitude, startLng: trip.start_longitude, endLat: trip.end_latitude, endLng: trip.end_longitude, startLocation: trip.start_location, endLocation: trip.end_location, startTime: trip.start_time, deviceId: trip?.current_location?.device_id, orders: trip.orders_picked, distance: trip.gps_kms, status: trip.status, driverPhone: trip.driver_phone, activeMobileFilter: activeMobileFilter, vehicleList: vehicleList } })
                                    }}
                                    sx={{
                                        ...card3DStyle,
                                        margin: '5px',
                                        padding: '10px',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        border: activeTripId === trip.id ? '2px solid white' : 'none'
                                    }}
                                >
                                    <Box display="flex" alignItems="stretch" sx={{ gap: '4px' }}>
                                        <LocationOnIcon sx={{ color: locationIconColor }} />
                                        <Typography fontWeight={700}>{trip.number_plate}</Typography>
                                        <Box sx={{ background: '#fff', borderRadius: '12px', paddingLeft: '7px', fontSize: '16px', fontWeight: 600, marginTop: '-1.5px' }}>
                                            {trip.driver_name}
                                        </Box>
                                        <Box sx={{ flex: 1 }} />
                                        {hasDriver && (
                                            <IconButton onClick={(e) => { e.stopPropagation(); handleCall(trip.driver_phone) }} sx={{ padding: '4px' }}>
                                                <PhoneIcon sx={{ color: '#4a81c5', fontSize: '30px' }} />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Box display="flex" alignItems="flex-start">
                                        <Typography sx={{ flex: 1, fontSize: '0.85rem', color: '#555', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                           {trip.start_time ? (
    <span>
        {trip.start_location && (
            <>
                <span style={{ fontWeight: 'bold', color: 'green' }}>Start: </span>
                {formatAddress(trip.start_location)}
            </>
        )}
        {trip.end_location && (
            <> 
                <span style={{ fontWeight: 'bold', color: 'red', marginLeft: '5px' }}>End: </span>
                {formatAddress(trip.end_location)}
            </>
        )}
    </span>
) : trip.start_location && trip.start_location !== 'Device not installed' ? (
    trip.start_location
) : null}
                                        </Typography>
                                        <Box display="flex" flexDirection="column" alignItems="center" ml={1}>
                                            {!trip.start_time && (
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRefreshLocation(e, trip?.vehicles_id || ''); }}>
                                                    <RefreshIcon sx={{ color: '#ed1b1b' }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box display="flex" alignItems="center" justifyContent="space-between" marginTop="4px" width="100%">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '10px', background: '#f4f4f4', fontSize: '0.75rem', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)' }}>
                                                <ShoppingCartIcon />
                                                {ordersDisplayValue}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', p: '4px 8px', borderRadius: '8px', background: '#f4f4f4', fontSize: '0.7rem' }}>
                                                <AccessTimeIcon fontSize="inherit" />
                                                <Box display="flex" flexDirection="column" lineHeight={1}>
                                                    <span style={{ fontWeight: 600 }}>{isEnded ? `${startParts.time} - ${endParts.time}` : startParts.time} | {displayDistance} km</span>
                                                </Box>
                                            </Box>
                                        </Box>
                                        {canEdit && (
                                            <IconButton size="small" sx={{ bgcolor: '#e3f2fd', borderRadius: '4px' }} onClick={(e) => { e.stopPropagation(); setSelectedEditTrip(trip); setEditModalOpen(true); }}>
                                                <i className="fas fa-edit" style={{ fontSize: '14px', color: '#1976d2' }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Box>
        </div>
    );
};

export default TripsMobileView;